/** Русские строки интерфейса. Эталонный словарь: здесь есть все ключи.
 *
 * Ключ строится как «экран.элемент»: так видно, где строка живёт, и
 * пропущенный перевод ищется по одному слову. Подстановки — в фигурных
 * скобках; формы числительных лежат в ключах `plural.*` через вертикальную
 * черту и подставляются функцией `pluralOf`.
 *
 * Объяснений подсказок и названий приёмов здесь нет: они собираются в ядре
 * (`sudoku_core/messages.py`) и приходят с сервера. Держать их ещё и тут
 * значит гарантированно разойтись — сначала в мелочах, потом по смыслу.
 */

export const ru: Record<string, string> = {
  // --- Общее ---------------------------------------------------------------
  'app.title': 'Sudokito',
  'app.loading': 'Готовим задачу…',
  'app.back': 'Назад',
  'app.cancel': 'Отмена',
  'app.error.login': 'Не удалось войти',
  'app.error.start': 'Не удалось начать партию',
  'app.error.move': 'Не удалось выполнить ход',
  'app.error.generic': 'Что-то пошло не так',

  'plural.days': 'день|дня|дней',
  'plural.players': 'игрок|игрока|игроков',
  'plural.times': 'раз|раза|раз',
  'plural.awards': 'награда|награды|наград',
  'plural.places': 'место|места|мест',
  'plural.minutes': 'минуту|минуты|минут',

  // --- Уровни --------------------------------------------------------------
  'level.easy': 'Лёгкий',
  'level.easy.hint': 'Разминка',
  'level.medium': 'Средний',
  'level.medium.hint': 'Спокойная партия',
  'level.hard': 'Сложный',
  'level.hard.hint': 'Нужно подумать',
  'level.expert': 'Эксперт',
  'level.expert.hint': 'Продвинутые приёмы',
  'level.master': 'Мастер',
  'level.master.hint': 'Только для упорных',

  // --- Стартовый экран -----------------------------------------------------
  'home.streak': 'Серия {days} {word}',
  'home.noStreak': 'Ещё нет серии',
  'home.continue': 'Продолжить партию',
  'home.continueMeta': '{level} · {time} · {filled} из 81 клетки',
  'home.newGame': 'Новая партия',
  'home.newGameHint': 'Выберите уровень',
  'home.more': 'Ещё',
  'home.noGame': 'Незаконченных партий нет',
  'home.noGameHint':
    'Начните с уровня ниже — прогресс сохранится сам, даже если закроете мессенджер.',
  'home.best': 'лучшее',
  'home.noRecord': 'нет рекорда',
  'home.rating': 'Рейтинг и награды',
  'home.ratingHint': 'Лига, сезон, достижения',
  'home.shop': 'Магазин',
  'home.shopHint': 'Темы и рамки',
  'home.settings': 'Настройки',
  'home.daily': 'Задача дня · {date}',
  'home.dailyTitle': 'Одна задача для всех',
  'home.dailyMeta': '{level} · ещё не решена',
  'home.dailySolvers': '{level} · её решают {count} чел.',
  'home.dailySolved': 'Решена за {time}',
  'home.dailyBetter': 'Лучше {percent}% игроков · возвращайтесь завтра',
  'home.dailyDone': 'Решена · возвращайтесь завтра',
  'home.play': 'Играть',
  'home.dailyPlay': 'Принять вызов',

  // --- Выбор режима --------------------------------------------------------
  'mode.title': '{level} · как играем?',
  'mode.calm': 'Спокойно',
  'mode.calmHint': 'Ошибки не ограничены',
  'mode.hardcore': 'Хардкор',
  'mode.hardcoreHint': 'Три ошибки — партия закончена',
  'mode.bonus': '{value} очков',
  'mode.start': 'Начать',

  // --- Партия --------------------------------------------------------------
  'game.title': 'Партия',
  'game.undo': 'Отменить',
  'game.redo': 'Вернуть',
  'game.erase': 'Стереть',
  'game.notes': 'Заметки',
  'game.hint': 'Подсказка',
  'game.notesHint': 'режим заметок — цифра уйдёт в карандаш',
  'game.offline': 'Нет связи — ходы сохраняются и отправятся сами',
  'game.mistakes': '✕ {used}/{limit}',
  'game.hintsUsed': 'подсказок {count}',
  'game.pause': 'Пауза',
  'game.pauseHint': 'Доска скрыта, таймер остановлен на {time}. Заполнено {filled} из 81.',
  'game.resume': 'Продолжить',
  'game.pinned': 'Закреплена {digit}',
  'game.abandon': 'Бросить партию',

  // --- Шторка подсказок ----------------------------------------------------
  'hints.title': 'Подсказки',
  'hints.check': 'Проверить ошибки',
  'hints.checkHint': 'Пометит красным все клетки, где цифра уже неверна',
  'hints.technique': 'Подсказать ход',
  'hints.techniqueHint': 'Откроет клетку и объяснит, почему там эта цифра',
  'hints.reveal': 'Открыть клетку',
  'hints.revealHint': 'Покажет цифру в клетке, которую выбрали вы',
  'hints.revealNeedsCell': 'Сначала выберите клетку на доске',
  'hints.autonotes': 'Заполнить заметки',
  'hints.autonotesHint': 'Расставит кандидатов во все пустые клетки',
  'hints.free': 'Бесплатно',
  'hints.freeIn': 'Бесплатная будет доступна через {hours} ч',
  'hints.notEnough': 'Не хватает {amount} ◆',
  'hints.notEnoughDaily': 'Не хватает {amount} ◆ — задача дня даёт до {reward}',
  'hints.earn': 'Как заработать кристаллы',

  // --- Плашка приёма -------------------------------------------------------
  'technique.tag': 'приём',
  'technique.opened': 'Открыта клетка {cell} · списано ◆ {cost}',
  'technique.openedFree': 'Открыта клетка {cell} · бесплатная подсказка дня',
  'technique.paid': 'Списано ◆ {cost}',
  'technique.got': 'Понятно, играю дальше',

  // --- Победа --------------------------------------------------------------
  'result.solved': 'Партия решена',
  'result.levelSolved': '{level} решён',
  'result.dailySolved': 'Задача дня решена',
  'result.points': 'очков',
  'result.time': 'Время',
  'result.mistakes': 'Ошибки',
  'result.hints': 'Подсказки',
  'result.speed': 'Множитель за скорость',
  'result.accuracy': 'Множитель за аккуратность',
  'result.mode': 'Множитель за хардкор',
  'result.coins': 'Кристаллы',
  'result.noHintBonus': 'Бонус за партию без подсказок',
  'result.record': 'Личный рекорд на «{level}»',
  'result.recordFrom': 'было {old} — стало {new}',
  'result.recordFirst': 'первый результат на этом уровне',
  'result.streak': 'Серия {days} {word} подряд',
  'result.achievement': 'Новое достижение: «{title}»',
  'result.again': 'Ещё партию',
  'result.toLevels': 'К выбору уровня',
  'result.share': 'Поделиться',
  'result.shareText': '{name} решил «{level}» за {time}. Попробуете эту же задачу?',
  'result.inviteText': 'Играю в судоку — присоединяйтесь',

  // --- Поражение -----------------------------------------------------------
  'loss.title': 'Попытки закончились',
  'loss.hint':
    'В хардкор-режиме их три. Доска сохранена — можно начать эту же задачу заново или взять уровень спокойнее. Серия дней не сброшена.',
  'loss.filled': 'Заполнено',
  'loss.filledValue': '{filled} из 81',
  'loss.progressCoins': 'Кристаллы за прогресс',
  'loss.restart': 'Начать эту задачу снова',

  // --- Рейтинг -------------------------------------------------------------
  'rating.title': 'Рейтинг и награды',
  'rating.tab.board': 'Рейтинг',
  'rating.tab.stats': 'Статистика',
  'rating.tab.awards': 'Награды',
  'rating.scope.total': 'За всё время',
  'rating.scope.weekly': 'За неделю',
  'rating.scope.daily': 'Задача дня',
  'rating.scope.best_time': 'Время по уровню',
  'rating.scope.friends': 'Друзья',
  'rating.you': '{name} · вы',
  'rating.delta': '+{count} {word} за неделю',
  'rating.deltaDown': '−{count} {word} за неделю',
  'rating.milestonePoints': 'до топ-{rank} нужно {gap} очков',
  'rating.milestoneTime': 'до топ-{rank} нужно {gap} с',
  'rating.refresh': 'Обновляется каждые {minutes} {word}',
  'rating.bestTimeHeader': 'Лучшее время · «{level}» · {count} {word}',
  'rating.timeNote': 'В таблице времени значение крупнее имени: сравнивают именно его.',
  'rating.empty': 'Здесь пока пусто',
  'rating.emptyHint': 'Решите партию — и попадёте в таблицу.',
  'rating.emptyDaily': 'Задачу дня ещё никто не решил',
  'rating.emptyDailyHint':
    'Решите первым — попадёте на первую строку и получите двойные кристаллы.',
  'rating.openDaily': 'Открыть задачу дня',

  // --- Статистика ----------------------------------------------------------
  'stats.games': 'партий сыграно',
  'stats.wins': 'побед · {percent}%',
  'stats.streak': 'дней подряд сейчас',
  'stats.bestStreak': 'лучшая серия',
  'stats.place': 'Место в общем зачёте',
  'stats.ofPlayers': 'из {count} {word}',
  'stats.noPlace': 'Пока вне зачёта',
  'stats.bestTimes': 'Лучшее время по уровням',
  'stats.notSolved': 'ещё не решён',

  // --- Награды -------------------------------------------------------------
  'awards.progress': 'Получено {done} из {total}',
  'awards.more': 'ещё {count} {word} ниже',

  // --- Магазин -------------------------------------------------------------
  'shop.title': 'Магазин',
  'shop.note': 'Оформление меняет вид доски — покупаете именно её.',
  'shop.chosen': 'Выбрано',
  'shop.choose': 'Выбрать',
  'shop.bought': 'Куплено · Выбрать',
  'shop.notEnough': 'Не хватает {amount} ◆',
  'shop.locked': 'Откроется на {days}-м дне серии',
  'shop.lockedProgress': '{current} из {total} дней',
  'shop.frames': 'Рамки аватара',
  'shop.framesNote': 'Рамку видят другие игроки в таблице рекордов.',

  // --- Кристаллы -----------------------------------------------------------
  'crystals.title': 'Кристаллы',
  'crystals.balance': 'на балансе',
  'crystals.howTo': 'Как пополнить',
  'crystals.daily': 'Задача дня',
  'crystals.dailyHint': 'Раз в сутки, без ошибок — вдвое больше',
  'crystals.dailyValue': 'до ◆ {max}',
  'crystals.streak': 'Серия дней',
  'crystals.streakHint': 'Каждый день подряд · сейчас {days} {word}',
  'crystals.streakValue': '◆ {amount} / день',
  'crystals.win': 'Победа в партии',
  'crystals.winHint': 'Чем выше уровень, тем больше',
  'crystals.awards': 'Достижения',
  'crystals.awardsHint': 'Осталось получить {count} из {total}',
  'crystals.noHints': 'Партия без подсказок',
  'crystals.noHintsHint': 'Бонус к начислению за победу',
  'crystals.range': '◆ {min}–{max}',
  'crystals.plus': '+ ◆ {amount}',
  'crystals.spend': 'Куда уходят',
  'crystals.spendHint':
    'Проверка ошибок ◆ {check} · подсказка хода ◆ {technique} · открыть клетку ◆ {reveal} · заметки ◆ {autonotes}',
  'crystals.openDaily': 'Открыть задачу дня',

  // --- Настройки -----------------------------------------------------------
  'settings.title': 'Настройки',
  'settings.digitFirst': 'Сначала цифра, потом клетки',
  'settings.digitFirstHint': 'Одну цифру ставим во много клеток',
  'settings.autoClear': 'Автоочистка заметок',
  'settings.autoClearHint': 'Убирать кандидатов после хода',
  'settings.haptics': 'Тактильный отклик',
  'settings.hapticsHint': 'Ввод, ошибка, победа',
  'settings.sound': 'Звук',
  'settings.soundHint': 'Короткие тоны на ввод и победу',
  'settings.notifications': 'Уведомления бота',
  'settings.notificationsHint': 'Задача дня и серия, которая вот-вот прервётся',
  'settings.language': 'Язык',
  'settings.techniques': 'Приёмы',
  'settings.techniquesHint': 'Изучено {done} из {total}',
  'settings.invite': 'Пригласить друга',
  'settings.inviteHint': 'Награда приходит за первую победу приглашённого',

  // --- Приёмы --------------------------------------------------------------
  'techniques.title': 'Приёмы',
  'techniques.progress': 'Изучено {done} из {total}',
  'techniques.source': 'копится из подсказок',
  'techniques.used': 'применён {count} {word}',
  'techniques.locked': 'Ещё не встречались',
  'techniques.willOpen': 'откроется подсказкой',
  'techniques.forever': 'Каждый приём открывается один раз и остаётся навсегда.',

  // --- Диалоги -------------------------------------------------------------
  'dialog.activeGame.title': 'Партия уже идёт',
  'dialog.activeGame.body':
    '{level}, {time}, заполнено {filled} из 81. Если начать новую, прогресс не сохранится.',
  'dialog.activeGame.continue': 'Продолжить текущую',
  'dialog.activeGame.new': 'Начать новую',
  'dialog.funds.title': 'Не хватает кристаллов',
  'dialog.funds.need': 'Нужно',
  'dialog.funds.have': 'есть',
  'dialog.funds.hint': 'Задача дня даёт до ◆ {daily}, серия дней — ◆ {streak} ежедневно.',
  'dialog.funds.earn': 'Как заработать',
  'dialog.funds.later': 'Позже',
  'dialog.puzzlePlayed': 'Эту задачу вы уже играли — сравнивать время было бы нечестно.',
}
